import { Key, useMemo, useState } from 'react'
import Styles from './BrandKitFontSelectorDrawer.module.scss'
import { FontPairingStyle } from 'constants/fontPairings'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { FontItem } from './BrandKitFontSelector'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { FontRole } from 'types'

interface Props extends DrawerProps {
  onFontSelectionChange: (key: Key | null) => void
  selectedFontStyle: FontPairingStyle | null
  googleFonts: (FontPairingStyle<FontRole.Title> | FontPairingStyle<FontRole.Body> | undefined)[]
  kitFonts: (FontPairingStyle<FontRole.Title> | FontPairingStyle<FontRole.Body> | undefined)[]
}

export const BrandKitFontSelectorDrawer = observer(
  ({ onFontSelectionChange, selectedFontStyle, googleFonts, kitFonts, ...props }: Props) => {
    const { drawersStore } = useRootStore()
    const [selectedFont, setSelectedFont] = useState<FontPairingStyle | null>(selectedFontStyle)
    const originalFontPairing = useMemo(() => selectedFont, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Drawer.Root {...props}>
        <Drawer.Portal>
          <Drawer.Content>
            <Drawer.InnerContent>
              <Drawer.Top>
                <Drawer.Back
                  variant="ghost"
                  position="left"
                  onPress={() => {
                    setSelectedFont(originalFontPairing)
                    onFontSelectionChange(
                      `${originalFontPairing?.style.font_family}--${originalFontPairing?.style.font_weight}`,
                    )
                    drawersStore.closeDrawer()
                  }}
                />
                <Drawer.Title asChild>
                  <Heading level={5}>Pick your Fonts</Heading>
                </Drawer.Title>
                <Drawer.Action
                  variant="ghost"
                  position="right"
                  onPress={() => {
                    drawersStore.closeDrawer()
                  }}
                >
                  Save
                </Drawer.Action>
              </Drawer.Top>
              <Drawer.Container padding="5px">
                <div className={Styles.fontListContainer}>
                  <div className={Styles.fontList}>
                    <div className={Styles.fontListHeader}>
                      <Text variant="label" color="var(--dark-40)">
                        From your website
                      </Text>
                    </div>
                    {kitFonts.map((font) => (
                      <div
                        onClick={() => {
                          if (font) setSelectedFont(font)
                          onFontSelectionChange(`${font?.style.font_family}--${font?.style.font_weight}`)
                        }}
                        key={`${font?.style.font_family}--${font?.style.font_weight}`}
                        className={cx(Styles.fontListItem, {
                          [Styles.selected]: font?.style.font_family === selectedFont?.style.font_family,
                        })}
                      >
                        <FontItem font={font} fontSize={16} />
                      </div>
                    ))}
                  </div>
                  <div className={Styles.separator} />
                  <div className={Styles.fontList}>
                    <div className={Styles.fontListHeader}>
                      <Text variant="label" color="var(--dark-40)">
                        Google Fonts
                      </Text>
                    </div>
                    {googleFonts.map((font) => (
                      <div
                        onClick={() => {
                          if (font) setSelectedFont(font)
                          onFontSelectionChange(`${font?.style.font_family}--${font?.style.font_weight}`)
                        }}
                        key={`${font?.style.font_family}--${font?.style.font_weight}`}
                        className={cx(Styles.fontListItem, {
                          [Styles.selected]: font?.style.font_family === selectedFont?.style.font_family,
                        })}
                      >
                        <FontItem font={font} fontSize={16} />
                      </div>
                    ))}
                  </div>
                </div>
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
